import { create } from 'zustand';
import { dmNeedsConnection } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { sessionAccess } from '../../lib/access.js';
import { apiHeaders, sessionInfo, useMembersStore } from './membersStore.js';

/**
 * Demandes de connexion (tasks/acces-copycampers-plan.md) : entre un membre
 * restreint (ex. Copycamper) et quelqu'un d'un autre périmètre, un message
 * privé demande une connexion acceptée. La règle est vérifiée par l'API ; ici,
 * seulement l'état affiché dans la fiche d'une personne et l'onglet Demandes.
 */

export type ConnectionStatus = 'pending' | 'accepted' | 'declined';

export interface Connection {
  userId: string;
  status: ConnectionStatus;
  direction: 'sent' | 'received';
  createdAt: string;
  respondedAt: string | null;
}

interface ConnectionsState {
  byUser: Map<string, Connection>;
  loaded: boolean;
  set: (list: Connection[]) => void;
  upsert: (c: Connection) => void;
  remove: (userId: string) => void;
}

export const useConnectionsStore = create<ConnectionsState>((set) => ({
  byUser: new Map(),
  loaded: false,
  set: (list) => set({ byUser: new Map(list.map((c) => [c.userId, c])), loaded: true }),
  upsert: (c) => set((s) => { const byUser = new Map(s.byUser); byUser.set(c.userId, c); return { byUser }; }),
  remove: (userId) => set((s) => { const byUser = new Map(s.byUser); byUser.delete(userId); return { byUser }; }),
}));

export async function loadConnections(): Promise<void> {
  if (sessionInfo().isGuest) return;
  try {
    const res = await fetch(`${API_BASE}/world/connections`, { headers: apiHeaders() });
    if (res.ok) useConnectionsStore.getState().set(await res.json() as Connection[]);
  } catch (err) {
    console.warn('[connections] liste indisponible:', err);
  }
}

/** Faut-il une connexion acceptée pour écrire à cette personne ? */
export function needsConnection(otherUserId: string): boolean {
  const { role, isGuest } = sessionInfo();
  if (isGuest) return false;
  const other = useMembersStore.getState().members.get(otherUserId);
  if (!other) return false;
  return dmNeedsConnection(
    { role, profileId: sessionAccess().ap ?? null },
    { role: other.role, profileId: other.accessProfile?.id ?? null },
  );
}

const ERRORS: Record<string, string> = {
  RETRY_LATER: 'Ta demande a été refusée récemment : réessaie dans quelques jours.',
  RATE_LIMITED: 'Tu as envoyé beaucoup de demandes aujourd’hui : réessaie demain.',
  REQUEST_NOT_FOUND: 'Cette demande n’existe plus.',
};

/** Action sur une connexion ; renvoie un message d'erreur à afficher, ou null. */
export async function connectionAction(
  userId: string,
  action: 'request' | 'accept' | 'decline' | 'remove',
): Promise<string | null> {
  const base = `${API_BASE}/world/connections/${encodeURIComponent(userId)}`;
  const url = action === 'accept' || action === 'decline' ? `${base}/${action}` : base;
  try {
    const res = await fetch(url, { method: action === 'remove' ? 'DELETE' : 'POST', headers: apiHeaders() });
    if (!res.ok) {
      const { error } = await res.json().catch(() => ({ error: '' })) as { error?: string };
      if (error === 'NOT_NEEDED') return null;
      return ERRORS[error ?? ''] ?? 'Impossible pour le moment, réessaie.';
    }
    if (action === 'remove') useConnectionsStore.getState().remove(userId);
    else useConnectionsStore.getState().upsert(await res.json() as Connection);
    return null;
  } catch {
    return 'Impossible pour le moment, réessaie.';
  }
}
